import {
  Typography,
  Button,
  Box,
  makeStyles,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { argoCDApiRef } from '@roadiehq/backstage-plugin-argo-cd';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import React, { useState, useCallback, useEffect } from 'react';
import { useArgocdConfig } from '../../argocd-enhanced/hooks/useArgocdConfig';
import { PipelineCards } from '../shared/PipelineCards';
import { PipelineRunsTable } from '../shared/PipelineRunsTable';
import { useTektonPipelines, WORKFLOW_TYPE, useOwnershipCheck } from '../shared';
import {
  TektonPipeline,
  TektonPipelineRun,
  getPipelineRunStatus,
  getTektonApiVersion,
} from '../types';

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoChip: {
    marginLeft: theme.spacing(1),
  },
  runsSection: {
    marginTop: theme.spacing(4),
  },
  arrayParamContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  arrayParamHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  arrayParamItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  arrayParamInput: {
    flex: 1,
  },
}));

interface ArgoCDDestination {
  server: string;
  namespace: string;
  clusterName?: string;
}

export const CustomWorkflowCards: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const kubernetesApi = useApi(kubernetesApiRef);
  const argoApi = useApi(argoCDApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const { instances } = useArgocdConfig();

  // Owner 检查：判断当前用户是否有权限执行 Pipeline
  const { canExecute, ownerCheckDone } = useOwnershipCheck();

  // ArgoCD 目标集群解析状态
  const [clusterLoading, setClusterLoading] = useState(true);
  const [clusterError, setClusterError] = useState<string>('');
  const [destination, setDestination] = useState<ArgoCDDestination | null>(null);
  const [backstageClusterName, setBackstageClusterName] = useState<string>('');

  // 执行对话框状态
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<TektonPipeline | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [arrayParamValues, setArrayParamValues] = useState<Record<string, string[]>>({});
  const [executing, setExecuting] = useState(false);

  const annotations = entity?.metadata?.annotations || {};
  const argoCDAppName = annotations['argocd/app-name'];
  const argoCDAppNamespace = annotations['argocd/app-namespace'] || 'argocd';
  const argoCDInstanceName = instances?.[0]?.name || 'default';
  const componentName = entity?.metadata?.name || '';
  // 完整 entityRef，用于后端权限校验
  const componentEntityRef = `component:${entity?.metadata?.namespace || 'default'}/${componentName}`;

  // 使用 useTektonPipelines Hook（在集群解析完成后）
  const {
    pipelines,
    pipelineRuns,
    pipelinesWithRuns,
    loading: pipelinesLoading,
    error: pipelinesError,
    executingPipelines,
    createPipelineRun,
    cancelPipelineRun,
  } = useTektonPipelines({
    componentName,
    componentEntityRef,
    argoCDAppName,
    clusterName: backstageClusterName,
    namespace: destination?.namespace || '',
    workflowType: WORKFLOW_TYPE.CUSTOM,
    pollInterval: 6000,
    enablePolling: !!backstageClusterName && !!destination?.namespace,
  });


  // 获取 ArgoCD Application 的目标集群和命名空间
  const fetchDestination = useCallback(async () => {
    if (!argoCDAppName) {
      setClusterError('未配置 argocd/app-name 注解');
      setClusterLoading(false);
      return null;
    }

    try {
      const appDetails = await argoApi.getAppDetails({
        url: `/argoInstance/${argoCDInstanceName}`,
        appName: argoCDAppName,
        appNamespace: argoCDAppNamespace,
        instance: argoCDInstanceName,
      });

      if (!appDetails) {
        throw new Error(`ArgoCD Application ${argoCDAppName} not found`);
      }

      const appAny = appDetails as any;
      const server = appAny.spec?.destination?.server || '';
      const clusterName = appAny.spec?.destination?.name;
      const namespace = appAny.spec?.destination?.namespace || 'default';

      const dest: ArgoCDDestination = { server, namespace, clusterName };
      setDestination(dest);
      return dest;
    } catch (err) {
      setClusterError(`获取 ArgoCD 应用失败: ${err}`);
      return null;
    }
  }, [argoCDAppName, argoCDAppNamespace, argoCDInstanceName, argoApi]);

  // 解析集群名称
  const resolveCluster = useCallback(async (
    destinationName: string
  ): Promise<{ clusterName: string; serverUrl: string } | null> => {
    try {
      const proxyUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(
        `${proxyUrl}/k8s-cluster-resolver/resolve?destinationName=${encodeURIComponent(destinationName)}`,
        { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.clusterName) {
          return { clusterName: data.clusterName, serverUrl: data.serverUrl };
        }
      }
      return null;
    } catch {
      return null;
    }
  }, [discoveryApi, identityApi]);

  // 初始化：解析 ArgoCD 目标集群
  useEffect(() => {
    let isMounted = true;

    const resolveDestinationCluster = async () => {
      if (!argoCDAppName) {
        setClusterError('未配置 argocd/app-name 注解');
        setClusterLoading(false);
        return;
      }

      setClusterLoading(true);
      const dest = await fetchDestination();
      
      if (!dest || !isMounted) {
        if (isMounted) setClusterLoading(false);
        return;
      }

      // 解析集群名称
      let clusterName: string | null = null;

      if (dest.clusterName) {
        const resolved = await resolveCluster(dest.clusterName);
        if (resolved) {
          clusterName = resolved.clusterName;
          if (isMounted) {
            setDestination(prev => prev ? { ...prev, server: resolved.serverUrl } : prev);
          }
        }
      }

      if (!clusterName && dest.server) {
        const resolved = await resolveCluster(dest.server);
        if (resolved) clusterName = resolved.clusterName;
      }

      if (!clusterName) {
        if (isMounted) {
          setClusterError(`无法在 Backstage 配置中找到匹配的集群: ${dest.clusterName || dest.server}`);
        }
      } else if (isMounted) {
        setBackstageClusterName(clusterName);
      }

      if (isMounted) setClusterLoading(false);
    };

    resolveDestinationCluster();

    return () => {
      isMounted = false;
    };
  }, [argoCDAppName, fetchDestination, resolveCluster]);

  // 打开执行对话框
  const openExecuteDialog = (pipeline: TektonPipeline) => {
    setSelectedPipeline(pipeline);
    const initialParams: Record<string, string> = {};
    const initialArrayParams: Record<string, string[]> = {};
    pipeline.spec.params?.forEach(param => {
      if (param.type === 'array') {
        const defaultVal = param.default;
        if (Array.isArray(defaultVal)) {
          initialArrayParams[param.name] = defaultVal.length > 0 ? defaultVal : [''];
        } else if (typeof defaultVal === 'string' && defaultVal) {
          initialArrayParams[param.name] = defaultVal.split(',').map(s => s.trim());
        } else {
          initialArrayParams[param.name] = [''];
        }
      } else {
        const defaultVal = param.default;
        initialParams[param.name] = Array.isArray(defaultVal) ? defaultVal.join(',') : (defaultVal || '');
      }
    });
    setParamValues(initialParams);
    setArrayParamValues(initialArrayParams);
    setExecuteDialogOpen(true);
  };

  // 打开执行对话框（带参数）
  const openExecuteDialogWithParams = async (pipeline: TektonPipeline, previousRun: TektonPipelineRun) => {
    setSelectedPipeline(pipeline);
    const initialParams: Record<string, string> = {};
    const initialArrayParams: Record<string, string[]> = {};

    pipeline.spec.params?.forEach(param => {
      if (param.type === 'array') {
        const defaultVal = param.default;
        if (Array.isArray(defaultVal)) {
          initialArrayParams[param.name] = defaultVal.length > 0 ? defaultVal : [''];
        } else if (typeof defaultVal === 'string' && defaultVal) {
          initialArrayParams[param.name] = defaultVal.split(',').map(s => s.trim());
        } else {
          initialArrayParams[param.name] = [''];
        }
      } else {
        const defaultVal = param.default;
        initialParams[param.name] = Array.isArray(defaultVal) ? defaultVal.join(',') : (defaultVal || '');
      }
    });

    let runParams = previousRun.spec?.params;
    if (!runParams && backstageClusterName && destination?.namespace) {
      try {
        // 根据 PipelineRun 的 apiVersion 确定使用的 Tekton API 版本
        const tektonApiVersion = getTektonApiVersion(previousRun.apiVersion);
        const response = await kubernetesApi.proxy({
          clusterName: backstageClusterName,
          path: `/apis/${tektonApiVersion}/namespaces/${destination.namespace}/pipelineruns/${previousRun.metadata.name}`,
        });
        if (response.ok) {
          const fullRun = await response.json();
          runParams = fullRun.spec?.params;
        }
      } catch { /* ignore */ }
    }

    runParams?.forEach((param: { name: string; value: string | string[] }) => {
      const pipelineParam = pipeline.spec.params?.find(p => p.name === param.name);
      if (pipelineParam?.type === 'array') {
        if (Array.isArray(param.value)) {
          initialArrayParams[param.name] = param.value.length > 0 ? param.value : [''];
        } else if (typeof param.value === 'string') {
          initialArrayParams[param.name] = [param.value];
        }
      } else {
        if (Array.isArray(param.value)) {
          initialParams[param.name] = param.value.join(',');
        } else {
          initialParams[param.name] = param.value || '';
        }
      }
    });

    setParamValues(initialParams);
    setArrayParamValues(initialArrayParams);
    setExecuteDialogOpen(true);
  };

  const closeExecuteDialog = () => {
    setExecuteDialogOpen(false);
    setSelectedPipeline(null);
    setParamValues({});
    setArrayParamValues({});
  };

  const addArrayParamItem = (paramName: string) => {
    setArrayParamValues(prev => ({
      ...prev,
      [paramName]: [...(prev[paramName] || []), ''],
    }));
  };

  const removeArrayParamItem = (paramName: string, index: number) => {
    setArrayParamValues(prev => ({
      ...prev,
      [paramName]: prev[paramName].filter((_, i) => i !== index),
    }));
  };

  const updateArrayParamItem = (paramName: string, index: number, value: string) => {
    setArrayParamValues(prev => ({
      ...prev,
      [paramName]: prev[paramName].map((item, i) => i === index ? value : item),
    }));
  };

  // 执行 Pipeline
  const executePipeline = async () => {
    if (!selectedPipeline || !destination || !backstageClusterName) return;

    setExecuting(true);

    try {
      const params: Array<{ name: string; value: string | string[] }> = [];
      Object.entries(paramValues).forEach(([name, value]) => {
        params.push({ name, value });
      });
      Object.entries(arrayParamValues).forEach(([name, values]) => {
        const filteredValues = values.filter(v => v.trim() !== '');
        if (filteredValues.length > 0) {
          params.push({ name, value: filteredValues });
        }
      });

      // 使用 Hook 提供的 createPipelineRun 方法
      await createPipelineRun(selectedPipeline, params);

      closeExecuteDialog();
    } catch (err) {
      setClusterError(`执行 Pipeline 失败: ${err}`);
    } finally {
      setExecuting(false);
    }
  };

  // 取消 PipelineRun
  const handleCancelPipelineRun = async (runName: string) => {
    if (!destination || !backstageClusterName) return;

    try {
      await cancelPipelineRun(runName);
    } catch (err) {
      setClusterError(`取消 PipelineRun 失败: ${err}`);
    }
  };

  const handleCancelPipeline = (pipeline: TektonPipeline) => {
    const runningRun = pipelineRuns.find(
      r => r.spec?.pipelineRef?.name === pipeline.metadata.name &&
        ['Running', 'Pending'].includes(getPipelineRunStatus(r))
    );
    if (runningRun) handleCancelPipelineRun(runningRun.metadata.name);
  };

  const handleRerun = (run: TektonPipelineRun) => {
    const pipeline = pipelines.find(p => p.metadata.name === run.spec?.pipelineRef?.name);
    if (pipeline) openExecuteDialogWithParams(pipeline, run);
  };

  const getServerDisplay = (server: string): string => {
    if (!server) return '未知';
    try {
      const url = new URL(server);
      return `${url.hostname}:${url.port || (url.protocol === 'https:' ? '443' : '80')}`;
    } catch {
      return server || '未知';
    }
  };

  const isClusterMatched = !!backstageClusterName;

  const renderClusterInfo = () => {
    if (!destination) return null;
    return (
      <>
        <Chip size="small" label={`集群名: ${destination.clusterName || '未知'}`} className={classes.infoChip} />
        <Chip size="small" label={`命名空间: ${destination.namespace}`} className={classes.infoChip} />
        <Chip size="small" label={`Server: ${destination.server ? getServerDisplay(destination.server) : '未知'}`}
          className={classes.infoChip} title={destination.server || ''} />
        <Chip
          size="small"
          label="使用文档"
          className={classes.infoChip}
          component="a"
          href="https://alidocs.dingtalk.com/i/nodes/m9bN7RYPWd6MQdedIqxbDRP7JZd1wyK0?doc_type=wiki_doc&iframeQuery=utm_source=portal&utm_medium=portal_recent&rnd=0.022058553024356664"
          target="_blank"
          clickable
          color="primary"
          style={{ backgroundColor: '#1976d2', color: '#fff' }}
        />
      </>
    );
  };

  const renderClusterWarning = () => {
    if (!destination || isClusterMatched) return null;
    return (
      <Box mt={2}>
        <Alert severity="warning">
          未匹配到集群配置！ArgoCD 目标集群 "{destination.clusterName || destination.server || '未知'}"
          未在 Backstage Kubernetes 配置中找到对应的集群，无法获取 Pipeline 信息。
        </Alert>
      </Box>
    );
  };

  // 合并加载状态
  const loading = clusterLoading || pipelinesLoading;
  const error = clusterError || pipelinesError;

  if (!argoCDAppName) {
    return (
      <Box>
        <Alert severity="info">
          未配置 ArgoCD 应用，请在 Component 注解中添加 <code>argocd/app-name</code>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 16 }}>加载自定义工作流...</Typography>
      </Box>
    );
  }

  if (error && pipelines.length === 0) {
    return (
      <Box>
        <Box className={classes.sectionTitle} mb={2}>{renderClusterInfo()}</Box>
        {renderClusterWarning()}
        {isClusterMatched && <Alert severity="info">{error}</Alert>}
      </Box>
    );
  }

  if (pipelines.length === 0) {
    return (
      <Box>
        <Box className={classes.sectionTitle} mb={2}>{renderClusterInfo()}</Box>
        {renderClusterWarning()}
        {isClusterMatched && (
          <Alert severity="info">当前 Component 中未发现 Pipeline 类型资源定义</Alert>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.sectionTitle} mb={2}>
        <Typography variant="h5">Pipelines ({pipelinesWithRuns.length})</Typography>
        {renderClusterInfo()}
      </Box>
      {renderClusterWarning()}

      <PipelineCards
        pipelinesWithRuns={pipelinesWithRuns}
        executingPipelines={executingPipelines}
        onExecute={openExecuteDialog}
        onCancel={handleCancelPipeline}
        showCancelButton={true}
        canExecute={canExecute}
        ownerCheckDone={ownerCheckDone}
      />

      <Box className={classes.runsSection}>
        <PipelineRunsTable
          runs={pipelineRuns}
          clusterName={backstageClusterName}
          pipelineNames={pipelines.map(p => p.metadata.name)}
          showFilters={true}
          showPagination={true}
          onRerun={handleRerun}
          showRerunButton={true}
        />
      </Box>

      <Dialog open={executeDialogOpen} onClose={closeExecuteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>执行 Pipeline: {selectedPipeline?.metadata.name}</DialogTitle>
        <DialogContent>
          {selectedPipeline?.spec.params && selectedPipeline.spec.params.length > 0 ? (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>参数配置</Typography>
              {selectedPipeline.spec.params.map(param => (
                param.type === 'array' ? (
                  <Box key={param.name} className={classes.arrayParamContainer}>
                    <Box className={classes.arrayParamHeader}>
                      <Typography variant="body2" color="textSecondary">
                        {param.name}
                        <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                          ({param.description || 'array 类型'})
                        </Typography>
                      </Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addArrayParamItem(param.name)} color="primary">
                        添加
                      </Button>
                    </Box>
                    {(arrayParamValues[param.name] || []).map((value, index) => (
                      <Box key={index} className={classes.arrayParamItem}>
                        <TextField
                          className={classes.arrayParamInput}
                          value={value}
                          onChange={(e) => updateArrayParamItem(param.name, index, e.target.value)}
                          variant="outlined"
                          size="small"
                          placeholder={`值 ${index + 1}`}
                        />
                        <IconButton size="small" onClick={() => removeArrayParamItem(param.name, index)}
                          disabled={(arrayParamValues[param.name] || []).length <= 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <TextField
                    key={param.name}
                    label={param.name}
                    helperText={param.description || `类型: ${param.type}`}
                    value={paramValues[param.name] || ''}
                    onChange={(e) => setParamValues(prev => ({ ...prev, [param.name]: e.target.value }))}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    size="small"
                  />
                )
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">此 Pipeline 无需配置参数</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExecuteDialog} disabled={executing}>取消</Button>
          <Button onClick={executePipeline} color="primary" variant="contained" disabled={executing}
            startIcon={executing ? <CircularProgress size={16} /> : <PlayArrowIcon />}>
            {executing ? '执行中...' : '执行'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
